import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { format } from 'date-fns';

import { InvoicesQuery, InvoiceStatus } from 'generated/graphql';
import BackButtonLayoutTitleAction from 'retired/BackButtonLayout/BackButtonLayoutTitleAction';
import { currencyFormat } from 'utils/currencyFormat';
import { renderInvoiceStatus } from 'utils/renderInvoiceStatus';

import { MarkInvoiceAsPaidModal } from './MarkInvoiceAsPaidModal';
import { InvoiceStatusChip } from './InvoiceStatusChip';
import { MarkInvoiceAsVoidedModal } from './MarkInvoiceAsVoidedModal';
import { MarkInvoiceAsSentModal } from './MarkInvoiceAsSentModal';

type Invoice = InvoicesQuery['invoices']['items'][0];

export const InvoiceDetails = ({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) => {
  const [markInvoiceAsPaidModalOpen, setMarkInvoiceAsPaidModalOpen] = useState(false);
  const [markInvoiceAsVoidedModalOpen, setMarkInvoiceAsVoidedModalOpen] = useState(false);
  const [markInvoiceAsSentModalOpen, setMarkInvoiceAsSentModalOpen] = useState(false);

  return (
    <BackButtonLayoutTitleAction title={`Invoice ${invoice.invoiceNumber}`} onBackClick={onClose}>
      <Paper style={{ marginTop: 24, padding: 16 }}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Typography variant="body2" lineHeight={2}>
              <span style={{ fontWeight: 500 }}>Status:</span> {renderInvoiceStatus(invoice.status)}
            </Typography>
            <Typography variant="body2" lineHeight={2}>
              <span style={{ fontWeight: 500 }}>Invoice Date:</span>{' '}
              {format(new Date(invoice.createdAt), 'MMM dd, yyyy')}
            </Typography>
            {invoice.paymentDueDate && (
              <Typography variant="body2" lineHeight={2}>
                <span style={{ fontWeight: 500 }}>Due Date:</span>{' '}
                {format(new Date(invoice.paymentDueDate), 'MMM dd, yyyy')}
              </Typography>
            )}
            <Box mt={2} display="flex" gap={2}>
              <Button
                variant="outlined"
                href={invoice.invoiceDocumentURL}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!invoice.invoiceDocumentURL}
              >
                View PDF
              </Button>
              <Button
                variant="outlined"
                href={invoice.disbursementsDocumentURL}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!invoice.disbursementsDocumentURL}
              >
                Download Disbursements
              </Button>
            </Box>
          </Box>
          <Box>
            <InvoiceStatusChip
              invoice={invoice}
              onChange={(status) => {
                if (status === InvoiceStatus.Voided) {
                  setMarkInvoiceAsVoidedModalOpen(true);
                } else if (status === InvoiceStatus.Paid) {
                  setMarkInvoiceAsPaidModalOpen(true);
                } else if (status === InvoiceStatus.Sent) {
                  setMarkInvoiceAsSentModalOpen(true);
                }
              }}
            />
          </Box>
        </Box>
      </Paper>
      <Paper style={{ marginTop: 24 }}>
        <TableContainer>
          <Table aria-label="invoice lines">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.invoiceLines.map((line) => (
                <TableRow key={line.itemLabel}>
                  <TableCell>{line.itemLabel}</TableCell>
                  <TableCell>{currencyFormat(line.amount, invoice.currencyCode)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} align="right">
                  Total Amount Due: {currencyFormat(invoice.amount, invoice.currencyCode)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <MarkInvoiceAsPaidModal
        invoiceId={invoice.id}
        open={markInvoiceAsPaidModalOpen}
        onClickClose={() => setMarkInvoiceAsPaidModalOpen(false)}
      />
      <MarkInvoiceAsVoidedModal
        invoiceId={invoice.id}
        open={markInvoiceAsVoidedModalOpen}
        onClickClose={() => setMarkInvoiceAsVoidedModalOpen(false)}
      />
      <MarkInvoiceAsSentModal
        invoiceId={invoice.id}
        open={markInvoiceAsSentModalOpen}
        onClickClose={() => setMarkInvoiceAsSentModalOpen(false)}
      />
    </BackButtonLayoutTitleAction>
  );
};
